import React from 'react';
import { useSelector } from 'react-redux';
import { Images, attributeTypes, OrgIntegrations, renderStaffName, staffStatusEnums, useModal } from 'utils';
import { SelectAutocompletePagination, SelectTypeAutocomplete } from 'components';
import { AiSummaryKind, formModelEnums } from '../../../constants';
import { signatureFillModeEnums, signatureFillSourceEnums, signatureFillSourceList } from '../../signatureFill.constants';
import { useFormBuilder } from '../../formContext';
import { CustomAiPromptModal } from './customAiPromptModal';
import { keepFirstRowOnly, stripMentions } from '../../../hooks/goalModeTable';
import { getConfiguredMinSelections, isMultiChoiceAttribute } from '../../minSelections';
import { hasInlineFields } from '../../../utils/inlineFields';

const GOAL_MODE_TYPES = [attributeTypes.RICH_TEXT, attributeTypes.TABLE];

/** Keys of the model's `listKey` tokens (`goal` / `target`) that are not general tokens. */
const tokenKeysOnlyIn = (formMeta, listKey) => {
    const generalKeys = new Set((formMeta?.general || []).map((f) => f?.defaultValue));
    return new Set((formMeta?.[listKey] || []).map((f) => f?.defaultValue).filter((key) => key && !generalKeys.has(key)));
};

/**
 * Goal Mode offers the goal tokens (`@GoalName`, …) in this one field's `@`
 * list, without turning the whole section into a goal section. Offered only on
 * RICH_TEXT / TABLE fields of a CLINICAL_NOTES template — the template model is
 * the only condition.
 *
 * Rendered by `BehaviorSection` and, since TABLE has no Behavior section, by
 * `TableColumnsSection`.
 */
export const GoalModeToggle = ({ selectedItem, handleUpdate }) => {
    const { formList } = useFormBuilder();
    const formSettings = useSelector((state) => state.forms.formSettings);
    const formMeta = formSettings?.defaultValuesMeta?.[formList?.model];
    const isClinicalNote = formList?.model === formModelEnums.CLINICALNOTES || !!formList?.models?.includes?.(formModelEnums.CLINICALNOTES);

    if (!isClinicalNote || !GOAL_MODE_TYPES.includes(selectedItem?.type)) return null;

    const handleToggle = (checked) => {
        handleUpdate('goalMode', checked);
        if (!checked) {
            // Target Mode lives under Goal Mode, so it goes off with it.
            const keys = tokenKeysOnlyIn(formMeta, 'goal');
            if (selectedItem?.properties?.targetMode) {
                handleUpdate('targetMode', false);
                tokenKeysOnlyIn(formMeta, 'target').forEach((key) => keys.add(key));
            }
            const stripped = stripMentions(selectedItem?.value, keys);
            if (stripped !== selectedItem?.value) handleUpdate('value', stripped);
            return;
        }
        if (selectedItem?.type !== attributeTypes.TABLE) return;
        const table = selectedItem?.properties?.table || {};
        const makeRow = () => (table.tableHead || []).reduce((row, col) => ({ ...row, [col.id]: '' }), { _rowId: `_row_${Date.now()}` });
        const next = keepFirstRowOnly(table, Array.isArray(selectedItem?.value) ? selectedItem.value : [], makeRow);
        handleUpdate('table', next.table);
        handleUpdate('value', next.body);
    };

    return (
        <div className="behavior-row">
            <div className="behavior-info">
                <span className="behavior-title">Goal Mode</span>
                <span className="behavior-desc">
                    {selectedItem?.type === attributeTypes.TABLE ? 'Use goal data in the table' : 'Use goal data in this field'}
                </span>
            </div>
            <label className="toggle-switch">
                <input type="checkbox" checked={!!selectedItem?.properties?.goalMode} onChange={(e) => handleToggle(e.target.checked)} />
                <span className="slider"></span>
            </label>
        </div>
    );
};

/**
 * Target Mode, under Goal Mode on a TABLE: the target tokens (`@TargetName`, …)
 * resolve, and on the note staff pick targets of the picked goals, with one row
 * per target instead of one per goal (`GoalModeTable`). Picking a target token in
 * the table turns it on (`TableElement`). Turning it off strips the target tokens
 * from the table.
 */
export const TargetModeToggle = ({ selectedItem, handleUpdate }) => {
    const { formList } = useFormBuilder();
    const formSettings = useSelector((state) => state.forms.formSettings);
    const formMeta = formSettings?.defaultValuesMeta?.[formList?.model];

    if (selectedItem?.type !== attributeTypes.TABLE || !selectedItem?.properties?.goalMode) return null;

    const handleToggle = (checked) => {
        handleUpdate('targetMode', checked);
        if (checked) return;
        const stripped = stripMentions(selectedItem?.value, tokenKeysOnlyIn(formMeta, 'target'));
        if (stripped !== selectedItem?.value) handleUpdate('value', stripped);
    };

    return (
        <div className="behavior-row">
            <div className="behavior-info">
                <span className="behavior-title">Target Mode</span>
                <span className="behavior-desc">One row per target, with target data</span>
            </div>
            <label className="toggle-switch">
                <input type="checkbox" checked={!!selectedItem?.properties?.targetMode} onChange={(e) => handleToggle(e.target.checked)} />
                <span className="slider"></span>
            </label>
        </div>
    );
};

/**
 * "Minimum selections" for CHECK_BOX and multi SELECT: a filled note needs at
 * least this many options chosen (see `minSelections.js`). Stored as
 * `properties.minSelections`; empty / 0 means no minimum.
 */
const MinSelectionsSetting = ({ selectedItem, handleUpdate }) => {
    if (!isMultiChoiceAttribute(selectedItem)) return null;
    const optionCount = selectedItem?.properties?.options?.length || 0;
    const configured = getConfiguredMinSelections(selectedItem);

    const handleChange = (raw) => {
        if (raw === '') return handleUpdate('minSelections', null);
        const next = Math.max(0, Math.floor(Number(raw)));
        if (!Number.isFinite(next)) return;
        handleUpdate('minSelections', next || null);
    };

    return (
        <div className="props-input-group" style={{ marginTop: 4 }}>
            <div className="props-label">Minimum selections</div>
            <input
                type="number"
                min={0}
                max={optionCount || undefined}
                step={1}
                placeholder="No minimum"
                className="custom-input"
                value={configured || ''}
                onChange={(e) => handleChange(e.target.value)}
            />
            <span className="behavior-desc">
                {configured > optionCount
                    ? `Only ${optionCount} option${optionCount === 1 ? '' : 's'} exist, so all of them will be needed once any is chosen.`
                    : configured
                    ? selectedItem?.properties?.required
                        ? `At least ${configured} of ${optionCount} options must be chosen before the note can be submitted.`
                        : `Optional: it can be left empty, but if any option is chosen, at least ${configured} must be.`
                    : 'Leave empty for no minimum.'}
            </span>
        </div>
    );
};

/** `showMinSelections: false` hides Minimum selections (inline fields in Rich Text). */
export const BehaviorSection = ({ selectedItem, handleUpdate, onOpenConditions, showMinSelections = true }) => {
    const { formList } = useFormBuilder();
    // AI Summary is only meaningful for clinical session notes. Frontend
    // flattens the form's `models` array into a single string `formList.model`
    // (see `createForm.js`), so we check the singular field.
    const isClinicalNoteForm = formList?.model === 'CLINICAL_NOTES';
    // Org-level master switch — admin can disable all AI features per org.
    const { aiBridge } = OrgIntegrations();

    // "Signature Required From" is offered only on SIGNATURE fields. The single
    // staff member picked here (stored as the whole object on
    // `properties.signerUser`) is the only user allowed to complete the signature.
    const signerUser = selectedItem?.properties?.signerUser || null;

    // Signature auto-fill mirrors one of the appointment's signatures, so it is
    // only offered on the note kinds whose resource IS an appointment.
    const isApptForm = formList?.model === formModelEnums.CLINICALNOTES || formList?.model === formModelEnums.OFFICENOTES;
    const signatureFill = selectedItem?.properties?.signatureFill || {};
    const isAutoSignature = signatureFill.mode === signatureFillModeEnums.AUTO;

    // Read Only is derived from the mode rather than chosen, so the toggle above
    // is shown locked while auto-fill is on (see `handleSignatureFillMode`).
    const readOnlyLocked = selectedItem?.type === attributeTypes.SIGNATURE && isApptForm && isAutoSignature;

    /**
     * Auto-filled signatures are never signed by hand, so switching the mode also
     * settles the two properties that only make sense for a hand-signed field:
     * the block becomes read-only, and the assigned-signer lock is dropped.
     * Switching back to manual restores an editable, unassigned field rather
     * than silently keeping the auto-mode lock.
     *
     * The three writes land in one state transition — `handleUpdate` derives
     * each one from the previous state — so the mode, the read-only flag and the
     * cleared signer are committed together.
     */
    const handleSignatureFillMode = (auto) => {
        handleUpdate('signatureFill', {
            mode: auto ? signatureFillModeEnums.AUTO : signatureFillModeEnums.MANUAL,
            source: signatureFill.source || signatureFillSourceEnums.CLIENT_GUARDIAN,
        });
        handleUpdate('readOnly', auto);
        if (auto && signerUser) handleUpdate('signerUser', null);
    };

    // Enforce one-AI-Summary-per-form at the UI level: if any OTHER attribute
    // in the form already has aiSummary on, lock the toggle here so the form
    // designer can't end up with two flagged fields.
    const otherHasAiSummary = (formList?.sections || []).some((section) =>
        (section?.attributes || []).some((attr) => attr?.id !== selectedItem?.id && !!attr?.properties?.aiSummary)
    );
    const thisHasAiSummary = !!selectedItem?.properties?.aiSummary;
    const thisHasAiAssist = !!selectedItem?.properties?.aiAssist;
    // AI writes the whole value as plain formatted text, which would drop inline fields.
    const thisHasInlineFields = selectedItem?.type === attributeTypes.RICH_TEXT && hasInlineFields(selectedItem?.value);
    const aiSummaryDisabled = otherHasAiSummary || thisHasAiAssist || (thisHasInlineFields && !thisHasAiSummary);
    const aiAssistDisabled = thisHasAiSummary || (thisHasInlineFields && !thisHasAiAssist);
    const { open } = useModal();
    const currentPrompt = selectedItem?.properties?.customPrompt || '';

    const openPromptModal = () => {
        open(<CustomAiPromptModal initialPrompt={currentPrompt} onSave={(text) => handleUpdate('customPrompt', text)} />);
    };

    if (selectedItem?.properties?.updateForbidden) {
        return null;
    }

    return (
        <div className="properties-section">
            <div className="section-header">
                <img src={Images.behaivorIcon} alt="behaivorIcon" />
                <span>BEHAVIOR</span>
            </div>

            {selectedItem?.type !== attributeTypes.GRAPH && (
                <>
                    {selectedItem?.type !== attributeTypes.TABLE && (
                        <div className="behavior-row">
                            <div className="behavior-info">
                                <span className="behavior-title">Required</span>
                                <span className="behavior-desc">User must fill this field</span>
                            </div>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={!!selectedItem?.properties?.required}
                                    onChange={(e) => handleUpdate('required', e.target.checked)}
                                />
                                <span className="slider"></span>
                            </label>
                        </div>
                    )}

                    {showMinSelections && <MinSelectionsSetting selectedItem={selectedItem} handleUpdate={handleUpdate} />}

                    <div className="behavior-row" style={readOnlyLocked ? { opacity: 0.5 } : {}}>
                        <div className="behavior-info">
                            <span className="behavior-title">Read Only</span>
                            <span className="behavior-desc">
                                {readOnlyLocked
                                    ? 'Auto-filled signatures are mirrored from the appointment and are never editable on the form.'
                                    : 'User cannot edit value'}
                            </span>
                        </div>
                        <label className="toggle-switch" style={readOnlyLocked ? { cursor: 'not-allowed' } : {}}>
                            <input
                                type="checkbox"
                                disabled={readOnlyLocked}
                                checked={!!selectedItem?.properties?.readOnly}
                                onChange={(e) => handleUpdate('readOnly', e.target.checked)}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>

                    <div className="behavior-row">
                        <div className="behavior-info">
                            <span className="behavior-title">Staff Only</span>
                            <span className="behavior-desc">Only staff members can see this field</span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={!!selectedItem?.properties?.staffOnly}
                                onChange={(e) => handleUpdate('staffOnly', e.target.checked)}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>

                    {onOpenConditions && (
                        <div className="behavior-row behavior-row-clickable" onClick={onOpenConditions}>
                            <div className="behavior-info">
                                <span className="behavior-title">Conditional Logic</span>
                                <span className="behavior-desc">Show/hide or modify field</span>
                            </div>
                            <img src={Images.arrowRight} alt="open" className="behavior-chevron" />
                        </div>
                    )}

                    <GoalModeToggle selectedItem={selectedItem} handleUpdate={handleUpdate} />

                    {selectedItem?.type === attributeTypes.RICH_TEXT && isClinicalNoteForm && aiBridge && (
                        <div className="behavior-row" style={otherHasAiSummary ? { opacity: 0.5 } : {}}>
                            <div className="behavior-info">
                                <span className="behavior-title">AI Summary</span>
                                <span className="behavior-desc">
                                    {otherHasAiSummary
                                        ? 'Another field already has AI Summary. Turn it off there first.'
                                        : thisHasInlineFields && !thisHasAiSummary
                                        ? 'This text has inline fields, which AI would overwrite. Remove them to use AI Summary.'
                                        : thisHasAiAssist
                                        ? 'This field has AI Assist enabled. Turn it off to use AI Summary.'
                                        : 'Auto-fill with AI-generated text on note creation. Only one field per form.'}
                                </span>
                            </div>
                            <label className="toggle-switch" style={aiSummaryDisabled ? { cursor: 'not-allowed' } : {}}>
                                <input
                                    type="checkbox"
                                    disabled={aiSummaryDisabled}
                                    checked={thisHasAiSummary}
                                    onChange={(e) => handleUpdate('aiSummary', e.target.checked ? AiSummaryKind.SESSION_SUMMARY : null)}
                                />
                                <span className="slider"></span>
                            </label>
                        </div>
                    )}

                    {/* Custom Prompt card — visible only when AI Summary is ON for this field */}
                    {selectedItem?.type === attributeTypes.RICH_TEXT && isClinicalNoteForm && aiBridge && thisHasAiSummary && (
                        <div className="ai-custom-prompt-card">
                            <div className="ai-custom-prompt-card-content">
                                <div className="ai-custom-prompt-card-left">
                                    <span className="ai-custom-prompt-card-title">Custom Prompt</span>
                                    <button className="ai-custom-prompt-card-action" onClick={openPromptModal}>
                                        {currentPrompt ? (
                                            <>
                                                <img src={Images.edit} alt="edit" />
                                                <span className="ai-custom-prompt-add-btn">Edit Prompt</span>
                                            </>
                                        ) : (
                                            <>
                                                <img src={Images.plus} alt="add" />
                                                <span className="ai-custom-prompt-add-btn">Add Prompt</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                                <span className="ai-custom-prompt-card-desc">Define how AI should generate the summary for this field</span>
                            </div>
                        </div>
                    )}

                    {selectedItem?.type === attributeTypes.RICH_TEXT && aiBridge && (
                        <div className="behavior-row" style={aiAssistDisabled ? { opacity: 0.5 } : {}}>
                            <div className="behavior-info">
                                <span className="behavior-title">AI Assist</span>
                                <span className="behavior-desc">
                                    {thisHasInlineFields && !thisHasAiAssist
                                        ? 'This text has inline fields, which AI would overwrite. Remove them to use AI Assist.'
                                        : aiAssistDisabled
                                        ? 'This field has AI Summary enabled. Turn it off to use AI Assist.'
                                        : "Show an AI button on this field while filling the form. Staff can write a free-form instruction and AI will rewrite the field's current value."}
                                </span>
                            </div>
                            <label className="toggle-switch" style={aiAssistDisabled ? { cursor: 'not-allowed' } : {}}>
                                <input
                                    type="checkbox"
                                    disabled={aiAssistDisabled}
                                    checked={thisHasAiAssist}
                                    onChange={(e) => handleUpdate('aiAssist', e.target.checked)}
                                />
                                <span className="slider"></span>
                            </label>
                        </div>
                    )}
                </>
            )}

            {selectedItem?.type === attributeTypes.SIGNATURE && isApptForm && (
                <>
                    <div className="properties-divider" />
                    <div className="table-props-section">
                        <div className="behavior-row">
                            <div className="behavior-info">
                                <span className="behavior-title">Auto-fill from appointment</span>
                                <span className="behavior-desc">Mirror a signature already captured on the appointment</span>
                            </div>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={isAutoSignature}
                                    onChange={(e) => handleSignatureFillMode(e.target.checked)}
                                />
                                <span className="slider"></span>
                            </label>
                        </div>

                        {isAutoSignature && (
                            <div className="props-input-group" style={{ marginTop: 4 }}>
                                <div className="props-label">Signature to show</div>
                                <SelectTypeAutocomplete
                                    disableClearable={true}
                                    name={'signatureFillSource'}
                                    handleSelect={(event) =>
                                        handleUpdate('signatureFill', { ...signatureFill, source: event.target.value })
                                    }
                                    defaultValue={signatureFill.source || signatureFillSourceEnums.CLIENT_GUARDIAN}
                                    list={signatureFillSourceList}
                                    renderValue={(i) => i?.name}
                                    uiType={'tableFilter'}
                                    noError={true}
                                />
                                <span className="behavior-desc">
                                    Filled automatically and kept in step with the appointment. Nobody signs this field on the form.
                                </span>
                            </div>
                        )}
                    </div>
                </>
            )}

            {selectedItem?.type === attributeTypes.SIGNATURE && !isAutoSignature && (
                <>
                    <div className="properties-divider" />
                    <div className="table-props-section">
                        <div className="behavior-row">
                            <div className="behavior-info">
                                <span className="behavior-title">Signature Required From</span>
                                <span className="behavior-desc">Choose who must sign this field</span>
                            </div>
                        </div>

                        <div className="props-input-group" style={{ marginTop: 4 }}>
                            <div className="props-label">Signer Role</div>
                            <SelectAutocompletePagination
                                defaultStatuses={[staffStatusEnums.ACTIVE]}
                                uiType={'tableFilter'}
                                placeholder={'All'}
                                name={'signerUser'}
                                handleSelect={(e) => handleUpdate('signerUser', e.target.full)}
                                defaultValue={signerUser?.id}
                                currentSelected={signerUser}
                                renderValue={(i) => renderStaffName(i)}
                                type={'staff'}
                                noError={true}
                            />
                            {signerUser && (
                                <span className="behavior-desc">
                                    Only <strong>{renderStaffName(signerUser)}</strong> can complete the required signature.
                                </span>
                            )}
                        </div>
                    </div>
                </>
            )}

            {selectedItem?.type === attributeTypes.TABLE && (
                <>
                    <div className="behavior-row">
                        <div className="behavior-info">
                            <span className="behavior-title">Show Header</span>
                            <span className="behavior-desc">Display table header row</span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={selectedItem?.properties?.table?.showHeader !== false}
                                onChange={(e) => {
                                    const currentTable = selectedItem?.properties?.table || {};
                                    handleUpdate('table', { ...currentTable, showHeader: e.target.checked });
                                }}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                    <div className="behavior-row">
                        <div className="behavior-info">
                            <span className="behavior-title">Show Borders</span>
                            <span className="behavior-desc">Show or hide table borders</span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={selectedItem?.properties?.table?.showBorders !== false}
                                onChange={(e) => {
                                    const currentTable = selectedItem?.properties?.table || {};
                                    handleUpdate('table', { ...currentTable, showBorders: e.target.checked });
                                }}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                </>
            )}

            {selectedItem?.type === attributeTypes.GRAPH && (
                <>
                    <div className="behavior-row">
                        <div className="behavior-info">
                            <span className="behavior-title">Show Mastery</span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={selectedItem?.properties?.showMastery !== false}
                                onChange={(e) => handleUpdate('showMastery', e.target.checked)}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                    <div className="behavior-row">
                        <div className="behavior-info">
                            <span className="behavior-title">Show Goal Event</span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={selectedItem?.properties?.showGoalEvent !== false}
                                onChange={(e) => handleUpdate('showGoalEvent', e.target.checked)}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                    <div className="behavior-row">
                        <div className="behavior-info">
                            <span className="behavior-title">Show Target Event</span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={selectedItem?.properties?.showTargetEvent !== false}
                                onChange={(e) => handleUpdate('showTargetEvent', e.target.checked)}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                    <div className="behavior-row">
                        <div className="behavior-info">
                            <span className="behavior-title">Show Client Event</span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={selectedItem?.properties?.showClientEvent !== false}
                                onChange={(e) => handleUpdate('showClientEvent', e.target.checked)}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                </>
            )}
        </div>
    );
};
